import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Stack,
  Avatar,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { collection, getDocs, getFirestore } from "firebase/firestore";

import Page from "../components/Page";
import Scrollbar from "../components/Scrollbar";
import SearchNotFound from "../components/SearchNotFound";
import { UserListHead, UserListToolbar } from "../sections/@dashboard/user";
import { fDate } from "../utils/formatTime";

const db = getFirestore();

const TABLE_HEAD = [
  { id: "firstName", label: "First Name", alignRight: false },
  { id: "lastName", label: "Last Name", alignRight: false },
];

function applySortFilter(array, query) {
  return query
    ? array.filter((member) => {
        const searchFields = [
          member.firstName?.toLowerCase(),
          member.lastName?.toLowerCase(),
          member.membershipType?.toLowerCase(),
          member.membershipStatus?.toLowerCase(),
        ];

        return searchFields.some((field) =>
          field?.includes(query.toLowerCase())
        );
      })
    : array;
}

export default function MemberDirectory() {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [filterName, setFilterName] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const membersSnapshot = await getDocs(collection(db, "members"));
      const membersList = membersSnapshot.docs.map((doc) => {
        const data = doc.data();

        const activeSubscription = data.subscriptions?.find(
          (sub) => sub.status === "ACTIVE"
        );

        return {
          id: doc.id,
          ...data,
          membershipStatus: activeSubscription ? "Active" : "Inactive",
          membershipType: activeSubscription
            ? activeSubscription.planName
            : "N/A",
          membershipExpiry: activeSubscription
            ? fDate(activeSubscription.endDate)
            : "N/A",
        };
      });

      setMembers(membersList);
      setFilteredMembers(membersList);
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  const handleFilterByName = (event) => {
    const query = event.target.value;
    setFilterName(query);

    const filtered = applySortFilter(members, query);
    setFilteredMembers(filtered);
  };

  const handleViewProfile = (member) => {
    setSelectedMember(member);
  };

  const handleCloseProfile = () => {
    setSelectedMember(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenMessage = (member) => {
    navigate("/dashboard/messages", {
      state: {
        recipientId: member.uid,
        recipientName: `${member.firstName} ${member.lastName}`,
        recipientEmail: member.email || "",
      },
    });
  };

  const emptyRows =
    page > 0
      ? Math.max(0, (1 + page) * rowsPerPage - filteredMembers.length)
      : 0;

  return (
    <Page title="Member Directory">
      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Member Directory
          </Typography>
        </Stack>

        <Card>
          <UserListToolbar
            filterName={filterName}
            onFilterName={handleFilterByName}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead headLabel={TABLE_HEAD} />
                <TableBody>
                  {filteredMembers
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((member) => (
                      <TableRow hover key={member.id} sx={{ cursor: "pointer" }}>
                        <TableCell>
                          <Avatar alt={member.firstName} src={member.profilePic} />
                        </TableCell>
                        <TableCell>{member.firstName}</TableCell>
                        <TableCell>{member.lastName}</TableCell>
                        <TableCell align="right">
                          {member.uid ? (
                            <Button
                              variant="outlined"
                              onClick={() => handleOpenMessage(member)}
                            >
                              Message Member
                            </Button>
                          ) : (
                            <Typography />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}

                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>

                {filteredMembers.length === 0 && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6}>
                        <SearchNotFound searchQuery={filterName} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filteredMembers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>

        {/* Member Profile Dialog */}
        <Dialog open={Boolean(selectedMember)} onClose={handleCloseProfile}>
          <DialogTitle>{`${selectedMember?.firstName} ${selectedMember?.lastName}`}</DialogTitle>
          <DialogContent>
            <Typography variant="body1">Email: Hidden</Typography>
            <Typography variant="body1">Phone: Hidden</Typography>
            <Typography variant="body1">
              Squash Player: {selectedMember?.squashPlayer}
            </Typography>
            <Typography variant="body1">
              Racquetball Player: {selectedMember?.racquetballPlayer}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseProfile}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
